import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useVoucherValidation from '../useVoucherValidation';
import BrokerCommissions from './BrokerCommissions';
import OfficeCommissions from './OfficeCommissions';
import PoolCommissions from './PoolCommissions';
import { updateDeal } from '../../../redux/actions/deal-actions';
import {
  selectCommissionsGrossCommissionTotal,
  selectTotalCommissionsForCommissions,
} from '../../../redux/selectors/voucher-selectors';

const PAGE_NUMBER = 3.3;
const MARKET_NAME_MAX_LENGTH = 255;

const CommissionsTab = () => {
  const dispatch = useDispatch();
  const {
    dealId, deal_type, voucher, cbre_commission_is_percent, commissions, commissionsState,
    salesforce_id, market_name, deal_life_cycle_stage, voucher_submission_status,
    cbreGrossCommission, netDeduction,
  } = useSelector(state => ({
    dealId: state.deal.deal?.id,
    deal_type: state.deal.deal?.deal_type,
    salesforce_id: state.deal.deal?.salesforce_id || '',
    market_name: state.deal.deal?.market_name || '',
    deal_life_cycle_stage: state.deal.deal?.deal_life_cycle_stage || '',
    voucher_submission_status: state.deal.deal?.voucher_submission_status || '',
    voucher: state.voucher.voucher,
    cbre_commission_is_percent: state.voucher.voucher?.cbre_commission_is_percent,
    commissions: state.commissions.commissions.filter(({ cbre_broker_type }) => !!cbre_broker_type),
    commissionsState: state.commissions.state,
    cbreGrossCommission: selectCommissionsGrossCommissionTotal(state),
    netDeduction: selectTotalCommissionsForCommissions(state),
  }));
  const { showPageValidation } = useVoucherValidation(voucher.id, PAGE_NUMBER);

  const [pursuitIdValue, setPursuitIdValue] = useState(salesforce_id);
  const [marketNameValue, setMarketNameValue] = useState(market_name);

  const isPursuitIdNotRequired = (
    ['Voucher Submitted', 'Voucher Resubmitted', 'Voucher Completed'].includes(voucher_submission_status) &&
    ['closed', 'Closed'].includes(deal_life_cycle_stage)
  );

  const isPursuitIdRequired = !isPursuitIdNotRequired && (
    deal_type === 'lease' && ((cbreGrossCommission || 0) >= 100000 || (netDeduction || 0) >= 100000)
  );

  const pursuitIdFormatError = pursuitIdValue && !/^SF-\d{10}$/.test(pursuitIdValue);
  const pursuitIdError = showPageValidation && isPursuitIdRequired && !pursuitIdValue;

  const handlePursuitIdBlur = () => {
    if ((pursuitIdValue || '') !== salesforce_id) {
      dispatch(updateDeal(dealId, { salesforce_id: pursuitIdValue || '' }));
    }
  };

  const handleMarketNameBlur = () => {
    const trimmed = (marketNameValue || '').slice(0, MARKET_NAME_MAX_LENGTH);
    if (trimmed !== market_name) {
      dispatch(updateDeal(dealId, { market_name: trimmed }));
    }
  };

  return (
    <div className="commissions-tab horizontal-overflow">
      <div className="pursuit-market-row">
        <div className={`voucher-field-group${(pursuitIdError || pursuitIdFormatError) ? ' voucher-field-group--error' : ''}`}>
          <label className="voucher-field-label">
            {isPursuitIdRequired && <span className="acc-required-asterisk">* </span>}Pursuit ID
          </label>
          <input
            type="text"
            className={`acc-input-text${(pursuitIdError || pursuitIdFormatError) ? ' acc-input-error' : ''}`}
            value={pursuitIdValue}
            onChange={e => setPursuitIdValue(e.target.value)}
            onBlur={handlePursuitIdBlur}
            placeholder="Pursuit ID"
          />
          {pursuitIdError && (
            <p className="acc-error-message">Pursuit ID is required</p>
          )}
          {pursuitIdFormatError && (
            <p className="acc-error-message">Pursuit ID must start with SF followed by hyphen and then 10 digits.</p>
          )}
        </div>
        <div className="voucher-field-group">
          <label className="voucher-field-label">Market Name</label>
          <input
            type="text"
            className="acc-input-text"
            value={marketNameValue}
            onChange={e => setMarketNameValue(e.target.value)}
            onBlur={handleMarketNameBlur}
            placeholder="Market Name"
            maxLength={MARKET_NAME_MAX_LENGTH}
          />
        </div>
      </div>
      <h2>Commissions</h2>
      {!voucher.is_early_invoice && !commissions.length && commissionsState === 'NONE' && (
        <p className="error-message">
          You must add at least one commission to this {voucher.is_early_invoice ? 'early invoice' : 'voucher'}
        </p>
      )}
      <BrokerCommissions
        dealId={dealId}
        deal_type={deal_type}
        voucherId={voucher.id}
        cbre_commission_is_percent={cbre_commission_is_percent}
        showValidation={showPageValidation}
        shouldAddAsLeadBroker={!commissions.length}
      />
      <OfficeCommissions
        dealId={dealId}
        voucherId={voucher.id}
        cbre_commission_is_percent={cbre_commission_is_percent}
        showValidation={showPageValidation}
        shouldAddAsLeadBroker={!commissions.length}
      />
      <PoolCommissions
        dealId={dealId}
        voucherId={voucher.id}
        cbre_commission_is_percent={cbre_commission_is_percent}
        showValidation={showPageValidation}
        shouldAddAsLeadBroker={!commissions.length}
      />
    </div>
  );
};


export default CommissionsTab;
